import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Calendar, FileText } from 'lucide-react';
import { CMSDashboardViewProps } from './cmsdashboard.props';
import { DashboardSkeleton } from '@/components/layout/CmsSkeletons';

const formatMKD = (amount: number) => amount.toLocaleString('mk-MK') + ' МКД';

const CMSDashboardView: React.FC<CMSDashboardViewProps> = ({
  isAdmin,
  loading,
  statsCards,
  recentRegistrations,
  eventSummaries,
  onNavigate,
}) => {
  if (loading) return <DashboardSkeleton />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">CMS Dashboard</h1>
          <p className="text-muted-foreground mt-1">Управувајте со вашата платформа за настани</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsCards.map((stat, index) => (
              <Card
                key={index}
                className={stat.path ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}
                onClick={() => stat.path && onNavigate(stat.path)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                      <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                      <p className="text-sm text-green-600 mt-1">{stat.change}</p>
                    </div>
                    <div className={`${stat.color} p-3 rounded-lg`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Брзи акции</CardTitle>
            <CardDescription>Најчести задачи за управување</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                className="h-auto py-4 flex flex-col items-center space-y-2"
                onClick={() => onNavigate('/cms/events')}
              >
                <Plus className="w-6 h-6" />
                <span>Додај нов настан</span>
              </Button>
              {isAdmin() && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-center space-y-2"
                  onClick={() => onNavigate('/cms/organizers')}
                >
                  <Plus className="w-6 h-6" />
                  <span>Додај организатор</span>
                </Button>
              )}
              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-center space-y-2"
                onClick={() => onNavigate('/cms/posts')}
              >
                <Plus className="w-6 h-6" />
                <span>Креирај пост</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Event Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Преглед по настани</CardTitle>
            <CardDescription>Регистрации и приход по настан</CardDescription>
          </CardHeader>
          <CardContent>
            {eventSummaries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p>Нема настани. Додајте го вашиот прв настан.</p>
                <Button className="mt-4" onClick={() => onNavigate('/cms/events')}>
                  <Plus className="w-4 h-4 mr-1" />Додај настан
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-medium text-muted-foreground">Настан</th>
                      <th className="pb-3 font-medium text-muted-foreground">Датум</th>
                      <th className="pb-3 font-medium text-muted-foreground text-right">Регистрации</th>
                      <th className="pb-3 font-medium text-muted-foreground text-right">Платени</th>
                      <th className="pb-3 font-medium text-muted-foreground text-right">Приход</th>
                      <th className="pb-3 font-medium text-muted-foreground text-right">Без попусти</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventSummaries.map((ev) => (
                      <tr key={ev.id} className="border-b last:border-0">
                        <td className="py-3 font-medium text-foreground">{ev.name}</td>
                        <td className="py-3 text-muted-foreground">
                          {new Date(ev.date).toLocaleDateString('mk-MK')}
                        </td>
                        <td className="py-3 text-right text-foreground">{ev.totalRegistrations}</td>
                        <td className="py-3 text-right text-foreground">{ev.paidRegistrations}</td>
                        <td className="py-3 text-right font-medium text-green-700">
                          {formatMKD(ev.netRevenue)}
                        </td>
                        <td className="py-3 text-right text-muted-foreground">
                          {formatMKD(ev.grossRevenue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Registrations Widget */}
        <Card>
          <CardHeader>
            <CardTitle>Последни регистрации</CardTitle>
            <CardDescription>Најнови регистрации за настани</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentRegistrations.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                  <p>Нема регистрации сеуште.</p>
                </div>
              ) : (
                recentRegistrations.map((reg) => (
                  <div key={reg.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium text-foreground">
                        {reg.firstName} {reg.lastName}
                      </p>
                      <p className="text-sm text-muted-foreground">{reg.email}</p>
                    </div>
                    <div className="text-right">
                      <span
                        className={`inline-block px-2 py-1 text-xs rounded ${
                          reg.paymentStatus === 'Paid'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {reg.paymentStatus}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">#{reg.bibNumber}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <Button
              variant="outline"
              className="w-full mt-4"
              onClick={() => onNavigate('/cms/registrations')}
            >
              Види ги сите регистрации
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CMSDashboardView;
